<template>
  <div
    ref="dropdownContainer"
    id="school-multi-select-container"
    class="position-relative school-multi-select-container"
    data-testid="school-multi-select"
  >
    <div @click="toggleDropdown" :class="{ disabled: disabled }">
      <div
        class="school-multi-select-action"
        :class="{
          active: dropdownOpen
        }"
        :title="displayText"
      >
        <span class="school-multi-select-action-text">{{ displayText }}</span>
        <i class="ri-arrow-down-s-line school-multi-select-arrow"></i>
      </div>
    </div>
    <div
      v-if="dropdownOpen"
      class="school-multi-select-dropdown ps__child--consume"
      data-testid="school-multi-select-dropdown"
    >
      <!-- SEARCH INPUT -->
      <div
        v-if="hasMultipleSchools"
        class="school-multi-select-search"
        @click.stop
      >
        <i class="ri-search-line school-multi-select-search-icon"></i>
        <input
          ref="searchInput"
          type="text"
          v-model="searchQuery"
          placeholder="Search schools..."
          class="school-multi-select-search-input"
          data-testid="school-search-input"
          @click.stop
        />
        <button
          v-if="searchQuery"
          type="button"
          @click.stop="clearSearch"
          class="school-multi-select-search-clear"
          data-testid="clear-search-button"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>
      <!-- SEARCH INPUT -->

      <!-- SELECT ALL OPTION -->
      <div
        v-if="hasMultipleSchools"
        class="school-multi-select-item school-multi-select-item-select-all"
        @click.stop="handleSelectAll"
      >
        <div class="school-multi-select-checkbox-wrapper">
          <input
            type="checkbox"
            :id="'select-all-checkbox'"
            :checked="isAllSelected"
            @change.prevent.stop
            class="school-multi-select-checkbox-input"
            data-testid="select-all-checkbox"
          />
          <div
            class="school-multi-select-checkbox-visual"
            :class="{
              checked: isAllSelected,
              indeterminate: isIndeterminate
            }"
          >
            <i
              v-if="isAllSelected"
              class="ri-check-line school-multi-select-check-icon"
            ></i>
            <i
              v-else-if="isIndeterminate"
              class="ri-subtract-line school-multi-select-check-icon"
            ></i>
          </div>
        </div>
        <label
          :for="'select-all-checkbox'"
          class="school-multi-select-label"
          @click.stop="handleSelectAll"
          >Select All</label
        >
      </div>

      <!-- SCHOOL LIST -->
      <ul class="school-multi-select-list">
        <li
          v-for="(school, index) in filteredSchools"
          :key="school.id || index"
          class="school-multi-select-item"
          @click.stop="
            !isSingleSchool &&
            handleSchoolToggle(school, !isSchoolSelected(school.id))
          "
        >
          <div class="school-multi-select-checkbox-wrapper">
            <input
              type="checkbox"
              :id="'school-' + school.id"
              :checked="isSchoolSelected(school.id)"
              :disabled="isSingleSchool"
              @change.prevent.stop
              @click.prevent.stop
              class="school-multi-select-checkbox-input"
              :data-testid="`school-checkbox-${school.id}`"
            />
            <div
              class="school-multi-select-checkbox-visual"
              :class="{
                checked: isSchoolSelected(school.id),
                disabled: isSingleSchool
              }"
            >
              <i
                v-if="isSchoolSelected(school.id)"
                class="ri-check-line school-multi-select-check-icon"
              ></i>
            </div>
          </div>
          <label
            :for="'school-' + school.id"
            class="school-multi-select-label"
            :class="{ 'school-multi-select-label-disabled': isSingleSchool }"
            :title="school.name"
            @click.stop="
              !isSingleSchool &&
              handleSchoolToggle(school, !isSchoolSelected(school.id))
            "
          >
            {{ school.name }}
          </label>
        </li>
      </ul>
      <!-- SCHOOL LIST -->

      <!-- CLEAR SELECTION -->
      <div v-if="hasMultipleSchools" class="school-multi-select-clear">
        <button
          type="button"
          @click.stop="handleClearSelection"
          class="school-multi-select-clear-button"
          data-testid="clear-selection-button"
        >
          <i class="ri-filter-off-line school-multi-select-clear-icon"></i>
          <span class="school-multi-select-clear-text">Clear Selection</span>
        </button>
      </div>
      <!-- CLEAR SELECTION -->

      <!-- APPLY BUTTON -->
      <div v-if="hasMultipleSchools" class="school-multi-select-apply">
        <button
          type="button"
          @click.stop="handleApplySelection"
          class="school-multi-select-apply-button"
          data-testid="apply-selection-button"
        >
          <i class="ri-check-line school-multi-select-apply-icon"></i>
          <span class="school-multi-select-apply-text">Apply</span>
        </button>
      </div>
      <!-- APPLY BUTTON -->
    </div>
  </div>
</template>

<script>
import { getThisMonthDateRangeForStore } from "@/helpers/advanceReportingDateUtils"
import { useGradeYears } from "@/composables/useGradeYears.js"
import moment from "moment-timezone"
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import { useStore } from "vuex"

export default {
  name: "SchoolMultiSelect",
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    schools: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select school(s)"
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:model-value"],
  setup(props, { emit }) {
    const store = useStore()
    const { currentSchoolYear } = useGradeYears()
    const dropdownOpen = ref(false)
    const dropdownContainer = ref(null)
    const searchInput = ref(null)
    const searchQuery = ref("")
    const selectedSchools = ref([])

    const selectedSchoolIds = computed(() => {
      return selectedSchools?.value?.map((school) => school?.id) || []
    })

    const isSchoolSelected = (schoolId) => {
      return selectedSchoolIds.value.includes(schoolId)
    }

    const filteredSchools = computed(() => {
      if (!searchQuery.value || !searchQuery.value.trim()) {
        return props.schools || []
      }

      const query = searchQuery.value.toLowerCase().trim()
      return (props.schools || []).filter((school) =>
        school?.name?.toLowerCase().includes(query)
      )
    })

    const isAllSelected = computed(() => {
      if (!filteredSchools.value?.length) return false
      const filteredSchoolIds = filteredSchools.value.map((s) => s.id)
      return (
        filteredSchoolIds.length > 0 &&
        filteredSchoolIds.every((id) => selectedSchoolIds.value.includes(id))
      )
    })

    const isIndeterminate = computed(() => {
      if (!filteredSchools.value?.length) return false
      const filteredSchoolIds = filteredSchools.value.map((s) => s.id)
      const selectedInFiltered = filteredSchoolIds.filter((id) =>
        selectedSchoolIds.value.includes(id)
      )
      return (
        selectedInFiltered.length > 0 &&
        selectedInFiltered.length < filteredSchoolIds.length
      )
    })

    const hasMultipleSchools = computed(() => {
      return (props.schools?.length || 0) > 1
    })

    const isSingleSchool = computed(() => {
      return (props.schools?.length || 0) === 1
    })

    const displayText = computed(() => {
      const selectedCount = props.modelValue?.length || 0

      if (selectedCount === 0) {
        return props.placeholder
      }

      if (selectedCount === 1) {
        return props.modelValue[0]?.name || props.placeholder
      }

      if (selectedCount === (props.schools?.length || 0)) {
        return "All Schools"
      }

      return `${props.modelValue[0]?.name || ""} + ${selectedCount - 1}`
    })

    const toggleDropdown = () => {
      if (props.disabled) return
      dropdownOpen.value = !dropdownOpen.value
      if (dropdownOpen.value) {
        nextTick(() => {
          if (searchInput.value && hasMultipleSchools.value) {
            searchInput.value.focus()
          }
        })
      } else {
        searchQuery.value = ""
      }
    }

    const handleSchoolToggle = (school, checked) => {
      if (props.disabled) return
      if (isSingleSchool.value) return

      let newSelection = [...(selectedSchools?.value || [])]

      if (checked) {
        if (!isSchoolSelected(school.id)) {
          newSelection.push({ id: school.id, name: school.name })
        }
      } else {
        newSelection = newSelection.filter((s) => s.id !== school.id)
      }

      selectedSchools.value = newSelection
    }

    const handleSelectAll = () => {
      if (props.disabled) return

      const filteredSchoolIds = filteredSchools.value.map((s) => s.id)

      if (isAllSelected.value) {
        // Deselect all filtered schools
        const newSelection = (selectedSchools?.value || []).filter(
          (school) => !filteredSchoolIds.includes(school.id)
        )
        selectedSchools.value = newSelection
      } else {
        // Select all filtered schools
        const newSelection = [...(selectedSchools?.value || [])]
        filteredSchools.value.forEach((school) => {
          if (!isSchoolSelected(school.id)) {
            newSelection.push({ id: school.id, name: school.name })
          }
        })
        selectedSchools.value = newSelection
      }
    }

    const clearSearch = () => {
      searchQuery.value = ""
      nextTick(() => {
        if (searchInput.value) {
          searchInput.value.focus()
        }
      })
    }

    const handleClearSelection = () => {
      if (props.disabled) return
      selectedSchools.value = []
    }

    const resetDateRangeToThisMonth = () => {
      const activeSchoolTimeZone =
        store.getters["schools/activeSchoolTimeZone"] || moment.tz.guess()

      const dateRange = getThisMonthDateRangeForStore(
        currentSchoolYear.value,
        activeSchoolTimeZone,
        store.getters["schools/activeSchool"]
      )

      store?.commit("advanceReportingStore/setSelectedDateRange", dateRange)
    }

    const handleApplySelection = () => {
      resetDateRangeToThisMonth()
      emit("update:model-value", selectedSchools.value)
      dropdownOpen.value = false
      searchQuery.value = ""
    }

    const handleClickOutside = (event) => {
      if (
        dropdownOpen.value &&
        dropdownContainer.value &&
        !dropdownContainer.value.contains(event.target)
      ) {
        dropdownOpen.value = false
        searchQuery.value = ""
      }
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue) {
          selectedSchools.value = [...(newValue || [])]
        } else {
          selectedSchools.value = []
        }
      },
      { deep: true }
    )

    onMounted(() => {
      document.addEventListener("click", handleClickOutside)
      selectedSchools.value = [...(props.modelValue || [])]
    })

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside)
    })

    return {
      dropdownOpen,
      dropdownContainer,
      searchInput,
      searchQuery,
      filteredSchools,
      selectedSchoolIds,
      isSchoolSelected,
      isAllSelected,
      isIndeterminate,
      hasMultipleSchools,
      isSingleSchool,
      displayText,
      toggleDropdown,
      handleSchoolToggle,
      handleSelectAll,
      handleClearSelection,
      clearSearch,
      handleApplySelection
    }
  }
}
</script>
